import React, { useMemo, useState } from 'react';
import { Button } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';

interface Props {
  data: any;
  /** Maximum characters before collapsing (default 300) */
  threshold?: number;
  defaultExpanded?: boolean;
  label?: string;
  /** Redact values for sensitive-looking keys before rendering. Default true. */
  redactSensitive?: boolean;
}

const SENSITIVE_KEY_RE = /(password|passwd|pwd|secret|token|api[_-]?key|apikey|authorization|private[_-]?key|credential|smtp[_-]?password|ssh[_-]?password|sshpassword)/i;
const INTERNAL_PATH_KEY_RE = /(storage[_-]?path|server[_-]?path|local[_-]?path|artifact[_-]?path|filesystem[_-]?path)/i;
const INTERNAL_PATH_RE = /(?:\/data\/artifacts\/|\/data\/tasks\/|\/tmp\/praxys[^/\s]*\/)([^\s)\]}>"']+)/gi;
const PRAXYS_PRODUCTION_ORIGIN = 'https://praxys.ai';

const appOrigin = (): string => {
  if (typeof window !== 'undefined' && window.location?.origin && !/yourdomain/i.test(window.location.origin)) {
    return window.location.origin;
  }
  return PRAXYS_PRODUCTION_ORIGIN;
};

const sanitizeString = (value: string): string => {
  let result = value.replace(
    /https?:\/\/(?:www\.)?yourdomain(?:\.[a-z0-9.-]+)?([^\s)\]}>"']*)/gi,
    (_match, path: string) => `${appOrigin()}${path || ''}`,
  );
  result = result.replace(
    INTERNAL_PATH_RE,
    (_match, filename: string) => filename.split('/').pop() || '[internal path hidden]',
  );
  return result;
};

function redactValue(value: any, key = ''): any {
  if (SENSITIVE_KEY_RE.test(key)) {
    if (value === undefined || value === null || value === '') return value;
    return '••••••••';
  }
  if (INTERNAL_PATH_KEY_RE.test(key)) {
    if (value === undefined || value === null || value === '') return value;
    return '[internal path hidden]';
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([childKey, childValue]) => [childKey, redactValue(childValue, childKey)]),
    );
  }
  if (typeof value === 'string') {
    return sanitizeString(value);
  }
  return value;
}

function prettyJson(data: any): string {
  try {
    if (typeof data === 'string') {
      try {
        return JSON.stringify(JSON.parse(data), null, 2);
      } catch {
        return sanitizeString(data);
      }
    }
    return JSON.stringify(data, null, 2);
  } catch {
    return sanitizeString(String(data));
  }
}

const JsonViewer: React.FC<Props> = ({
  data,
  threshold = 300,
  defaultExpanded = false,
  label = 'Details',
  redactSensitive = true,
}) => {
  const safeData = useMemo(() => (redactSensitive ? redactValue(data) : data), [data, redactSensitive]);
  const rendered = prettyJson(safeData);
  const isLong = rendered.length > threshold;
  const [expanded, setExpanded] = useState(defaultExpanded || !isLong);

  if (!safeData || rendered === 'null' || rendered === '{}' || rendered === '[]') {
    return <span style={{ color: '#555', fontSize: 11 }}>—</span>;
  }

  return (
    <div>
      {isLong && (
        <Button
          type="link"
          size="small"
          icon={expanded ? <DownOutlined /> : <RightOutlined />}
          onClick={() => setExpanded(e => !e)}
          style={{ padding: 0, fontSize: 11, color: '#58a6ff', marginBottom: 4 }}
        >
          {expanded ? 'Collapse' : label}
        </Button>
      )}
      {expanded && (
        <pre
          style={{
            background: '#0d1117',
            border: '1px solid #1e2736',
            borderRadius: 4,
            padding: '8px 10px',
            fontSize: 11,
            color: '#8b949e',
            overflow: 'auto',
            maxHeight: 300,
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {rendered}
        </pre>
      )}
    </div>
  );
};

export default JsonViewer;
